import { HStack, SegmentGroup as SG, Text, VStack } from '@chakra-ui/react';
import type { ComponentProps } from 'react';
import type { IconType } from 'react-icons';
import { SEGMENTED_CONTROL_INDICATOR_SHADOW } from '@/components/ui/shadows.ts';

type ChakraMaxW = ComponentProps<typeof VStack>['maxW'];
type ChakraColor = ComponentProps<typeof Text>['color'];

export interface AppSwitchOption<T extends string = string> {
	label: string;
	value: T;
	icon?: IconType;
}

export interface AppSwitchProps<T extends string = string> {
	/** Label displayed above the switch */
	label?: string;
	/** The two options to toggle between */
	options: [AppSwitchOption<T>, AppSwitchOption<T>];
	/** Currently selected value */
	value: T;
	/** Callback when selection changes */
	onValueChange: (value: T) => void;
	/** Size of the segment group */
	size?: 'sm' | 'md' | 'lg';
	/** Maximum width of the switch container (default 320px) */
	maxW?: ChakraMaxW;
	/** Selected indicator background. Defaults to surface.default. */
	selectedBg?: ComponentProps<typeof SG.Indicator>['bg'];
	/** Selected option text/icon color. */
	selectedColor?: ChakraColor;
	/** Unselected option text/icon color. */
	unselectedColor?: ChakraColor;
	/** Segment group background. Defaults to bg.muted. */
	bg?: ComponentProps<typeof SG.Root>['bg'];
}

/**
 * Binary toggle switch built on top of Chakra SegmentGroup.
 * Accepts exactly two options and renders a pill-shaped selector.
 */
export const AppSwitch = <T extends string = string>({
	label,
	options,
	value,
	onValueChange,
	size = 'sm',
	maxW = '320px',
	selectedBg = 'surface.default',
	selectedColor,
	unselectedColor,
	bg = 'bg.muted',
}: AppSwitchProps<T>) => {
	return (
		<VStack gap={2} align="stretch" w="full" maxW={maxW}>
			{label && (
				<Text textStyle="body.sm" fontWeight="medium" textAlign="center">
					{label}
				</Text>
			)}

			<SG.Root
				value={value}
				onValueChange={(details) => onValueChange((details.value as T) ?? options[0].value)}
				size={size}
				w="full"
				rounded="full"
				p={1}
				bg={bg}
			>
				{options.map((option) => (
					<SG.Item
						key={option.value}
						value={option.value}
						w="100%"
						cursor="pointer"
						rounded="full"
						h="auto"
						minH={{ base: 10, md: 8 }}
					>
						<SG.ItemText
							textStyle="body.sm"
							fontWeight="medium"
							color={option.value === value ? selectedColor : unselectedColor}
						>
							<HStack gap={1}>
								{option.icon && <option.icon />}
								<span>{option.label}</span>
							</HStack>
						</SG.ItemText>
						<SG.ItemHiddenInput />
					</SG.Item>
				))}
				<SG.Indicator
					rounded="full"
					bg={selectedBg}
					boxShadow={SEGMENTED_CONTROL_INDICATOR_SHADOW}
					top="1"
					bottom="1"
					height="auto"
				/>
			</SG.Root>
		</VStack>
	);
};
